/**
 * SEO — Módulo SEO (tráfego orgânico).
 *
 * Reaproveita Layout, BowtieVisual, LeadsMap e MetricFilterBar do dashboard
 * pago. A diferença está nos dados: vêm de /api/seo/* e excluem qualquer
 * tráfego pago (gclid NULL/vazio).
 *
 * Quando o Google Search Console for integrado, este componente não precisa
 * mudar — basta o backend acrescentar campos no payload (impressions, ctr,
 * posição média, queries, etc.) e o front consome incrementalmente.
 */

import { useState } from 'react'
import Layout from '../components/Layout.jsx'
import BowtieVisual from '../components/BowtieVisual.jsx'
import MetricFilterBar from '../components/MetricFilterBar.jsx'
import KpiCard from '../components/KpiCard.jsx'
import PageState from '../components/PageState.jsx'
import PageFooter from '../components/PageFooter.jsx'
import LeadsMap from '../components/LeadsMap.jsx'
import SEOBreakdowns from '../components/SEOBreakdowns.jsx'
import { useViewMode } from '../hooks/useViewMode.js'
import { useApp } from '../context/AppContext.jsx'
import { useSEO } from '../hooks/useSEO.js'
import { useSEOWidgets } from '../hooks/useSEOWidgets.js'
import {
  SEO_METRIC_FILTERS,
  SEO_METRIC_LABEL,
  SEO_DEFAULT_METRIC,
} from '../lib/seoMetricFilters.js'

export default function SEO() {
  const { mode } = useViewMode()
  const { period, selectedClient } = useApp()
  const [activeMetric, setActiveMetric] = useState(SEO_DEFAULT_METRIC)

  const { data, loading: dataLoading, error: dataError, refetch } = useSEO(
    period.start, period.end, selectedClient?.id,
  )

  const { data: widgets, loading: widgetsLoading } = useSEOWidgets(
    period.start, period.end, selectedClient?.id, activeMetric,
  )

  const loading = dataLoading
  const error = dataError

  const kpisBase = data
    ? [
        data.kpis.visits,
        data.kpis.leads,
        data.kpis.conversion,
        data.kpis.revenue,
      ].map((k, i) => ({ ...k, key: ['visits', 'leads', 'conversion', 'revenue'][i] }))
    : []

  return (
    <Layout breadcrumb={[{ label: 'Marketing' }, { label: 'SEO' }]} title="SEO" showPeriod>
      <div className="dash-shell">
        <PageState loading={loading} error={error} loadingText="Carregando dados orgânicos..." onRetry={refetch} />

        {!loading && !error && data && (
          <>
            <div className="kpi-grid kpi-grid-4">
              {kpisBase.map((k) => (
                <KpiCard key={k.key} kpi={k} mode={mode} />
              ))}
            </div>

            <BowtieVisual data={data.funnel} title="Funil orgânico" />

            <MetricFilterBar
              active={activeMetric}
              onChange={setActiveMetric}
              disabled={widgetsLoading}
              items={SEO_METRIC_FILTERS}
              ariaLabel="Filtrar widgets SEO por métrica"
            />

            <div className={`seo-main-grid ${widgetsLoading ? 'is-loading' : ''}`}>
              <LeadsMap
                data={widgets?.leadsGeo ?? data.funnel.leadsGeo ?? []}
                metricLabel={SEO_METRIC_LABEL[activeMetric] || activeMetric}
              />

              <SEOBreakdowns
                sources={widgets?.sources ?? data.funnel.sources ?? []}
                devices={widgets?.osBreakdown ?? data.funnel.osBreakdown ?? []}
                pages={widgets?.pages ?? data.funnel.pages ?? []}
                cities={widgets?.cities ?? data.funnel.cities ?? []}
                loading={widgetsLoading}
              />
            </div>

            <PageFooter text={`Dados orgânicos · ${data.freshness.label}`} onRefresh={refetch} />
          </>
        )}
      </div>
    </Layout>
  )
}
